import { useEffect, useState } from "react";
import { addRecent } from "../lib/recentlyViewed";
import { favSavedToast } from "../lib/favorite";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { api, photoUrl, errText, flyerUrl, showsFlyer } from "../lib/api";
import { priceLabel, PROVINCE_NAME, CATEGORY_LABEL, isIso } from "../lib/constants";
import { IsoBadge, IsoPlaceholder } from "../components/IsoBadge";
import { IsoAlertOwnerCard } from "../components/IsoAlertOwnerCard";
import { IsoDemandTag } from "../components/IsoDemandTag";
import { LostFoundBadge } from "../components/LostFoundBadge";
import { LostFlyerButtons, FlyerDownloadButtons } from "../components/LostFlyerButtons";
import { FlyerShare } from "../components/FlyerShare";
import { ShareAdButton } from "../components/ShareAdButton";
import { FollowPrompt } from "../components/FollowPrompt";
import { ReunitedBanner, ReunitedButton } from "../components/Reunited";
import { LostBoost } from "../components/LostBoost";
import { SightingsMap } from "../components/SightingsMap";
import { ReunionStoryCard } from "../components/ReunionStoryCard";
import { ReunionShare } from "../components/ReunionShare";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { RoleAvatar, StaffTag } from "../components/RoleAvatar";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { ReportDialog } from "../components/ReportDialog";
import { useLanguage } from "../context/LanguageContext";
import { LostFoundFacts } from "../components/LostFoundFields";
import { MicrochipTip } from "../components/MicrochipTip";
import { catLabel } from "../lib/i18n";
import { BannerAd } from "../components/BannerAd";
import { HomePopup } from "../components/HomePopup";
import { CategoryChanger } from "../components/CategoryChanger";
import { LostFoundId, CopyIdButton } from "../components/LostFoundId";
import { FeatureAdButton } from "../components/FeatureAdButton";
import { SafetyNote } from "../components/SafetyNote";
import { useListingTranslation, ListingTranslateButton, TranslatedNote } from "../components/ListingTranslate";

const PET_CATS = ["dogs", "cats", "birds", "reptiles", "aquatics", "small-pets", "livestock", "rescue"];
const PET_ONE = {
  en: { dogs: "dog", cats: "cat", birds: "bird", reptiles: "reptile", aquatics: "fish", "small-pets": "small pet", livestock: "farm animal", rescue: "rescue pet" },
  fr: { dogs: "chien", cats: "chat", birds: "oiseau", reptiles: "reptile", aquatics: "poisson", "small-pets": "petit animal", livestock: "animal de ferme", rescue: "animal à adopter" },
};
import {
  MapPin, Heart, BadgeCheck, Eye, Calendar, Mail, Phone, Send, ArrowLeft, Loader2, Shield, Clock, Check, BellRing,
} from "lucide-react";

export default function ListingDetail() {
  const { id } = useParams();
  const location = useLocation();
  const [followOpen, setFollowOpen] = useState(!!location.state?.justPosted);
  const closeFollow = () => { setFollowOpen(false); window.history.replaceState({}, ""); };
  const navigate = useNavigate();
  const { user, setAuthOpen } = useAuth();
  const { t: tr, lang } = useLanguage();
  const [listing, setListing] = useState(null);
  const trans = useListingTranslation(id, lang, listing ? `${listing.title}\n${listing.description}` : "", listing);
  const [active, setActive] = useState(0);
  const [fav, setFav] = useState(false);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [showPhone, setShowPhone] = useState(false);

  useEffect(() => {
    api.get(`/listings/${id}`).then((r) => { setListing(r.data); setFav(r.data.is_favorited); if (r.data.status === "active") addRecent(r.data.id); })
      .catch(() => toast.error("Listing not found"));
  }, [id, user?.user_id]);

  if (!listing) return (
    <div className="max-w-6xl mx-auto px-4 py-10 grid lg:grid-cols-3 gap-8">
      <Skeleton className="lg:col-span-2 h-96 rounded-2xl" /><Skeleton className="h-96 rounded-2xl" />
    </div>
  );

  const withFlyer = showsFlyer(listing);
  const flyerLang = lang === "fr" ? "fr" : "en";
  const flyerSrc = (w) => flyerUrl(listing, { lang: flyerLang, w, v: listing.flyer_phone || "" });
  const photos = [...(withFlyer ? ["__flyer__"] : []), ...(listing.photos || [])];
  const slideSrc = (p, w) => (p === "__flyer__" ? flyerSrc(w) : photoUrl(p));
  const seller = listing.seller || {};
  const mine = user && user.user_id === listing.user_id;
  const isStaff = user && ["admin", "moderator"].includes(user.role);

  const toggleFav = async () => {
    if (!user) return setAuthOpen(true);
    try {
      fav ? await api.delete(`/favorites/${id}`) : await api.post(`/favorites/${id}`);
      if (!fav) favSavedToast(listing, lang);
      setFav(!fav);
    } catch { toast.error("Could not update"); }
  };

  const renew = async () => {
    setRenewing(true);
    try {
      const { data } = await api.post(`/listings/${id}/renew`);
      setListing(data);
      toast.success("Listing renewed for 30 days!");
    } catch (e) { toast.error(errText(e)); }
    finally { setRenewing(false); }
  };

  const sendInquiry = async () => {
    if (!user) return setAuthOpen(true);
    if (!msg.trim()) return toast.error("Write a message first");
    setSending(true);
    try {
      const { data } = await api.post(`/listings/${id}/inquire`, { message: msg });
      toast.success(data.email_sent ? "Message sent — seller notified by email!" : "Message sent to seller!");
      setMsg("");
      navigate(`/dashboard?tab=messages&c=${data.conversation_id}`);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSending(false);
    }
  };

  const priceText = listing.reunited_at ? tr("lfr_back_home") : listing.lf_type ? tr(listing.lf_type === "lost" ? "lf_tab_lost" : "lf_tab_found")
    : listing.price_type === "free"
    ? tr("ld_free")
    : (listing.price_type === "contact" || listing.price == null)
      ? tr("ld_contact_price")
      : priceLabel(listing);

  const templates = isIso(listing)
    ? (lang === "fr" ? ["J'en ai un qui pourrait vous convenir!", "Puis-je vous envoyer des photos?", "Quel est votre budget?"] : ["I have one that might be a match!", "Can I send you some photos?", "What's your budget?"])
    : [tr("ld_tpl_available"), tr("ld_tpl_visit"), tr("ld_tpl_health")];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      {!mine && !isStaff && listing.status === "active" && PET_CATS.includes(listing.category) && (
        <HomePopup page="listing" ctx={{ id: listing.id, city: listing.city, breed: listing.breed || (PET_ONE[lang] || PET_ONE.en)[listing.category] }} />
      )}
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-slate-500 hover:text-[#12234A] text-sm mb-4" data-testid="back-button"><ArrowLeft size={16} /> {tr("ld_back")}</button>

      {listing.status === "pending" && (
        <div className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-xl px-4 py-3" data-testid="pending-banner">
          <Clock size={16} className="mt-0.5 shrink-0" /> <span><strong>{tr("ld_pending_title")}</strong> {tr("ld_pending_body")}</span>
        </div>
      )}
      {listing.status === "rejected" && (
        <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-testid="rejected-banner">
          <Shield size={16} className="mt-0.5 shrink-0" /> <span><strong>{tr("ld_rejected_title")}</strong> {listing.reject_reason ? `${tr("ld_rejected_reason")} ${listing.reject_reason}` : tr("ld_rejected_body")}</span>
        </div>
      )}

      <ReunitedBanner listing={listing} />

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left: gallery + details */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden">
            <div className="relative aspect-[16/10] bg-slate-100">
              {photos.length ? (
                <img src={slideSrc(photos[active] ?? photos[0], 900)} alt={photos[active] === "__flyer__" ? `${listing.title} flyer` : listing.title} className="w-full h-full object-contain" data-testid="gallery-main" />
              ) : isIso(listing) ? <IsoPlaceholder big /> : <div className="w-full h-full flex items-center justify-center text-slate-300">{tr("ld_no_photo")}</div>}
              {isIso(listing) && <IsoBadge size="lg" testid="detail-iso-badge" />}
            </div>
            {photos.length > 1 && (
              <div className="flex gap-2 p-3 overflow-x-auto no-scrollbar">
                {photos.map((p, i) => (
                  <button key={`${p}-${i}`} onClick={() => setActive(i)} data-testid={p === "__flyer__" ? "thumb-flyer" : `thumb-${i}`} className={`relative h-16 w-20 rounded-lg overflow-hidden shrink-0 border-2 ${active === i ? "border-[#F5551E]" : "border-transparent"}`}>
                    <img src={slideSrc(p, 200)} alt="" className={`w-full h-full object-cover ${p === "__flyer__" ? "object-top" : ""}`} />
                    {p === "__flyer__" && <span className="absolute bottom-0 inset-x-0 bg-[#12234A]/85 text-white text-[10px] font-bold text-center py-0.5">{tr("lf_flyer_thumb")}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <FollowPrompt listing={listing} open={followOpen} onClose={closeFollow} />
          {withFlyer && (
            <div className="space-y-3 -mt-2">
              <FlyerDownloadButtons listing={listing} lang={flyerLang} />
              <FlyerShare listing={listing} lang={flyerLang} />
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <LostFoundBadge listing={listing} />
                  <span className="text-xs font-semibold text-[#F5551E] uppercase tracking-wider">{catLabel(lang, listing.category, CATEGORY_LABEL[listing.category])}</span>
                </div>
                <div className="flex items-center gap-3 flex-wrap mt-1">
                  <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#12234A]" data-testid="detail-title">{trans.active ? trans.active.title : listing.title}</h1>
                  <LostFoundId listing={listing} size="lg" testid="detail-lf-id" />
                  <CopyIdButton listing={listing} fr={lang === "fr"} />
                </div>
                <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-500">
                  <span className="flex items-center gap-1"><MapPin size={15} /> {listing.city}, {PROVINCE_NAME[listing.province]}</span>
                  <span className="flex items-center gap-1"><Eye size={15} /> {listing.views} {tr("ld_views")}</span>
                </div>
                {isStaff && listing.category !== "lost-found" && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="staff-category-row">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Staff · category</span>
                    <CategoryChanger listing={listing} size="md" onChange={(c) => setListing((l) => ({ ...l, category: c, ...(c === "iso" ? { price: null, price_type: "contact" } : {}) }))} />
                  </div>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                {listing.status === "active" && <ShareAdButton listing={listing} />}
              <button onClick={toggleFav} data-testid="detail-favorite-btn" className="h-11 w-11 rounded-full border border-slate-200 flex items-center justify-center hover:border-[#F5551E] transition-colors shrink-0">
                <Heart size={20} className={fav ? "fill-[#F5551E] text-[#F5551E]" : "text-slate-500"} />
              </button>
              </div>
            </div>

            {!isIso(listing) && <p className={`font-display text-3xl font-extrabold mt-4 ${listing.reunited_at ? "text-emerald-600" : "text-[#F5551E]"}`} data-testid="detail-price">{priceText}</p>}

            {(listing.breed || listing.age || listing.species) && (
              <div className="grid grid-cols-3 gap-3 mt-5">
                {listing.species && <Attr label={tr("ld_species")} value={listing.species} />}
                {listing.breed && <Attr label={tr("ld_breed")} value={listing.breed} />}
                {listing.age && <Attr label={tr("ld_age")} value={listing.age} />}
              </div>
            )}

            {listing.lf_type && <LostFoundFacts listing={listing} />}
            {listing.lf_type === "found" && !listing.reunited_at && <MicrochipTip className="mt-4" />}

            <div className="mt-6">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                <h3 className="font-display font-bold text-[#12234A]">{tr("ld_description")}</h3>
                {(listing.title || listing.description) && <ListingTranslateButton t={trans} />}
              </div>
              <p className="text-slate-600 leading-relaxed whitespace-pre-wrap" data-testid="detail-description">{(trans.active ? trans.active.description : listing.description) || tr("ld_no_description")}</p>
              <TranslatedNote t={trans} />
            </div>
            {listing.lf_type && listing.status === "active" && !listing.reunited_at && <LostFlyerButtons key={`${user?.user_id || ""}-${listing.flyer_phone || ""}`} listing={listing} />}
            {listing.lf_type && (mine || isStaff) && <ReunitedButton listing={listing} onChange={(v) => setListing((l) => ({ ...l, reunited_at: v }))} />}
            {mine && <FeatureAdButton listing={listing} fr={lang === "fr"} />}
            {mine && isIso(listing) && <div className="mt-4"><IsoAlertOwnerCard listing={listing} fr={lang === "fr"} /></div>}
            {mine && !isIso(listing) && listing.status === "active" && <IsoDemandTag listing={listing} fr={lang === "fr"} />}
            {mine && listing.alerted_count != null && (
              <div className="mt-3 rounded-xl bg-red-50 border border-red-100 px-3 py-2.5 flex items-start gap-2" data-testid="alerted-count">
                <BellRing size={16} className="text-red-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-bold text-[#12234A]" data-testid="alerted-count-value">{listing.alerted_count === 0 ? tr("al_count_zero") : listing.alerted_count === 1 ? tr("al_count_one") : tr("al_count").replace("{n}", listing.alerted_count)}</p>
                  <p className="text-xs text-slate-500">{tr("al_hint")}</p>
                  {listing.lf_type === "lost" && listing.status === "active" && !listing.reunited_at && <LostBoost listing={listing} onChange={(d) => setListing((l) => ({ ...l, ...d }))} />}
                </div>
              </div>
            )}
          </div>
          {listing.reunited_at && listing.status === "active" && (listing.reunion_story?.photo || listing.reunion_story?.note) && <ReunionShare listing={listing} />}
          {listing.reunited_at && (mine || isStaff) && <ReunionStoryCard listing={listing} onChange={(v) => setListing((l) => ({ ...l, reunion_story: v }))} />}
          {listing.lf_type && <SightingsMap listing={listing} canManage={mine || isStaff} />}
        </div>

        {/* Right: seller + contact */}
        <div className="space-y-5 lg:sticky lg:top-20 self-start">
          <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
            <Link to={`/seller/${seller.user_id}`} className="flex items-center gap-3 group" data-testid="seller-link">
              <RoleAvatar user={{ role: seller.role, picture: seller.picture, name: seller.name }} size={48} />
              <div>
                <p className="font-semibold text-[#12234A] group-hover:text-[#F5551E] transition-colors flex items-center gap-1">
                  {seller.name}
                  {(seller.verified_seller || seller.verified_business || seller.is_verified_seller || seller.verified_breeder) && (
                    <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-emerald-600" title="Verified" data-testid="verified-badge-inline">
                      <Check size={11} strokeWidth={3} className="text-white" />
                    </span>
                  )}
                </p>
                <p className="text-xs text-slate-400">{seller.seller_type} · {tr("ld_view_profile")}</p>
              </div>
            </Link>

            {seller.presence === "active" && (
              <div className="mt-3 flex items-center gap-2 bg-emerald-50 text-emerald-700 text-sm font-semibold rounded-xl px-3 py-2" data-testid="seller-reachable-nudge">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
                <span>{tr("ld_active_now")} · <span className="font-medium">{tr("ld_reachable")}</span></span>
              </div>
            )}

            {(seller.role === "admin" || seller.role === "moderator") && (
              <div className="mt-4"><StaffTag role={seller.role} /></div>
            )}

            {(seller.verified_business || seller.verified_seller || seller.is_verified_seller || seller.verified_breeder) && (
              <div className="mt-4 flex flex-wrap gap-2" data-testid="verified-badge-detail">
                {seller.verified_breeder && (
                  <span className="inline-flex items-center gap-1.5 bg-purple-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full" data-testid="detail-verified-breeder">
                    <BadgeCheck size={16} className="text-white" /> {tr("lc_verified_breeder")}
                  </span>
                )}
                {seller.verified_business && (
                  <span className="flex items-center gap-2 bg-amber-500 text-white text-sm font-semibold rounded-xl px-3 py-2" data-testid="verified-badge-business">
                    <BadgeCheck size={16} className="text-white" /> {tr("lc_verified_business")}
                  </span>
                )}
                {(seller.verified_seller || (!seller.verified_business && seller.is_verified_seller)) && (
                  <span className="flex items-center gap-2 bg-emerald-600 text-white text-sm font-semibold rounded-xl px-3 py-2" data-testid="verified-badge-seller">
                    <BadgeCheck size={16} className="text-white" /> {tr("lc_verified_seller")}
                  </span>
                )}
              </div>
            )}

            {mine ? (
              <div className="mt-5 space-y-2">
                {listing.is_expired && (
                  <div className="flex items-start gap-2 bg-red-50 text-red-700 text-sm rounded-xl px-3 py-2 mb-1" data-testid="expired-owner-banner">
                    <Clock size={16} className="mt-0.5 shrink-0" /> {tr("ld_expired_owner")}
                  </div>
                )}
                {listing.is_expired && (
                  <Button onClick={renew} disabled={renewing} className="w-full rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-2" data-testid="renew-listing-btn">
                    {renewing ? <Loader2 className="animate-spin" size={18} /> : <Clock size={16} />} {tr("ld_renew_30")}
                  </Button>
                )}
                <Button onClick={() => navigate("/dashboard")} className="w-full rounded-xl bg-[#12234A] text-white" data-testid="manage-listing-btn">{tr("ld_manage_dash")}</Button>
                <p className="text-xs text-center text-slate-400">{tr("ld_your_listing")}</p>
              </div>
            ) : listing.is_expired ? (
              <div className="mt-5 flex items-start gap-2 bg-slate-100 text-slate-600 text-sm rounded-xl px-3 py-3" data-testid="expired-banner">
                <Clock size={16} className="mt-0.5 shrink-0" /> {tr("ld_expired_public")}
              </div>
            ) : (
              <>
                <div className="mt-5">
                  <h3 className="font-display font-bold text-[#12234A] mb-2 flex items-center gap-2"><Send size={16} /> {tr("ld_contact_seller")}</h3>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {templates.map((t) => (
                      <button key={t} onClick={() => setMsg(t)} data-testid="msg-template" className="text-xs bg-slate-100 hover:bg-[#FFF4F0] hover:text-[#F5551E] text-slate-600 px-2.5 py-1 rounded-full transition-colors">{t}</button>
                    ))}
                  </div>
                  <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="inquiry-message" rows={3} className="rounded-xl" placeholder={tr("ld_placeholder")} />
                  <Button onClick={sendInquiry} disabled={sending} data-testid="send-inquiry-btn" className="w-full mt-2 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">
                    {sending ? <Loader2 className="animate-spin" size={18} /> : <Send size={16} />} {tr("ld_send")}
                  </Button>
                </div>

                {(listing.show_email || listing.show_phone) && (
                  <div className="mt-5 pt-5 border-t border-slate-100 space-y-2">
                    {listing.show_email && (
                      showEmail ? (
                        <a href={`mailto:${listing.contact_email}`} className="flex items-center gap-2 text-[#12234A] font-medium text-sm" data-testid="revealed-email"><Mail size={16} className="text-[#F5551E]" /> {listing.contact_email}</a>
                      ) : (
                        <Button variant="outline" onClick={() => setShowEmail(true)} data-testid="reveal-email-btn" className="w-full rounded-xl gap-2"><Mail size={16} /> {tr("ld_show_email")}</Button>
                      )
                    )}
                    {listing.show_phone && (
                      showPhone ? (
                        <a href={`tel:${listing.contact_phone}`} className="flex items-center gap-2 text-[#12234A] font-medium text-sm" data-testid="revealed-phone"><Phone size={16} className="text-[#F5551E]" /> {listing.contact_phone}</a>
                      ) : (
                        <Button variant="outline" onClick={() => setShowPhone(true)} data-testid="reveal-phone-btn" className="w-full rounded-xl gap-2"><Phone size={16} /> {tr("ld_show_phone")}</Button>
                      )
                    )}
                  </div>
                )}
              </>
            )}
            <SafetyNote category={listing.category} />
            <div className="mt-3 pt-3 border-t border-slate-100 flex justify-center">
              <ReportDialog listingId={listing.id} />
            </div>
          </div>
          <BannerAd variant="rectangle" />
        </div>
      </div>
    </div>
  );
}

const Attr = ({ label, value }) => (
  <div className="bg-slate-50 rounded-xl p-3">
    <p className="text-xs text-slate-400">{label}</p>
    <p className="font-semibold text-[#12234A] text-sm mt-0.5">{value}</p>
  </div>
);
